import React, { useState, useEffect } from 'react';
import { X, Cookie, Settings, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as ga from '@/utils/googleAnalytics';

interface CookiePreferences {
  essential: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CookieConsent: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always true, cannot be disabled
    functional: false,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Small delay before showing for better UX
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const savePreferences = async (prefs: CookiePreferences) => {
    localStorage.setItem('cookieConsent', JSON.stringify(prefs));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
    setShowSettings(false);
    
    // Simple backend tracking - just record that consent was given
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        // Only track for logged-in users
        fetch('/api/track-cookie-consent', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            analytics_enabled: prefs.analytics,
            marketing_enabled: prefs.marketing
          })
        }).catch(() => {
          // Silently fail - don't block user experience
          console.log('Cookie consent tracking failed');
        });
      }
    } catch (error) {
      // Silently fail - cookie consent should work even if backend fails
    }
    
    // Initialize analytics/marketing scripts based on preferences
    if (prefs.analytics) {
      // Initialize Google Analytics
      ga.initGA();
      console.log('Analytics cookies enabled');
    }
    if (prefs.marketing) {
      // Initialize marketing cookies (Facebook Pixel, etc.)
      console.log('Marketing cookies enabled');
    }
  };

  const acceptAll = () => {
    const allEnabled = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(allEnabled);
    savePreferences(allEnabled);
  };

  const rejectNonEssential = () => {
    const essentialOnly = {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    setPreferences(essentialOnly);
    savePreferences(essentialOnly);
  };

  const saveCustomPreferences = () => {
    savePreferences(preferences);
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <>
          {/* Cookie Banner */}
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-4 left-4 right-4 sm:bottom-6 sm:left-auto sm:right-6 sm:w-[450px] z-[9999]"
          >
            <div className="bg-white rounded-xl shadow-xl border-2 border-blue-600 overflow-hidden">
              {!showSettings ? (
                // Main Banner View - Compact
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Cookie className="w-5 h-5 text-blue-600" />
                    <h2 className="text-base font-semibold text-gray-900">Cookie Preferences</h2>
                    <button
                      onClick={() => setShowBanner(false)}
                      className="ml-auto text-gray-400 hover:text-gray-600"
                      aria-label="Close"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  <p className="text-sm text-gray-600 mb-3">
                    We use cookies to enhance your experience, analyze site traffic, and personalize content.{' '}
                    <Link to="/cookies" className="text-blue-600 hover:text-blue-700 underline">
                      Cookie Policy
                    </Link>
                  </p>

                  <div className="flex gap-2">
                    <button
                      onClick={acceptAll}
                      className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                    >
                      Accept All
                    </button>
                    <button
                      onClick={rejectNonEssential}
                      className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                    >
                      Reject Non-Essential
                    </button>
                    <button
                      onClick={() => setShowSettings(true)}
                      className="px-3 py-2 bg-yellow-400 text-gray-900 rounded-lg text-sm font-medium hover:bg-yellow-500 transition-colors flex items-center gap-1"
                    >
                      <Settings className="w-3.5 h-3.5" />
                      Customize
                    </button>
                  </div>
                </div>
                ) : (
                  // Settings View - Compact
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-base font-semibold text-gray-900">Customize Settings</h2>
                      <button
                        onClick={() => setShowSettings(false)}
                        className="text-gray-400 hover:text-gray-600"
                        aria-label="Back"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="space-y-3 mb-4 max-h-[300px] overflow-y-auto">
                      {/* Essential Cookies */}
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h3 className="text-sm font-medium text-gray-900">Essential</h3>
                            <p className="text-xs text-gray-600">Required for basic functions</p>
                          </div>
                          <div className="ml-3">
                            <div className="w-10 h-5 bg-blue-600 rounded-full relative cursor-not-allowed opacity-75">
                              <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-white rounded-full" />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Functional Cookies */}
                      <div className="bg-white border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h3 className="text-sm font-medium text-gray-900">Functional</h3>
                            <p className="text-xs text-gray-600">Personalization & preferences</p>
                          </div>
                          <button
                            onClick={() => setPreferences(prev => ({ ...prev, functional: !prev.functional }))}
                            className="ml-3"
                          >
                            <div className={`w-10 h-5 rounded-full relative transition-colors ${
                              preferences.functional ? 'bg-blue-600' : 'bg-gray-300'
                            }`}>
                              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                                preferences.functional ? 'translate-x-5' : 'translate-x-0.5'
                              }`} />
                            </div>
                          </button>
                        </div>
                      </div>

                      {/* Analytics Cookies */}
                      <div className="bg-white border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h3 className="text-sm font-medium text-gray-900">Analytics</h3>
                            <p className="text-xs text-gray-600">Usage statistics & performance</p>
                          </div>
                          <button
                            onClick={() => setPreferences(prev => ({ ...prev, analytics: !prev.analytics }))}
                            className="ml-3"
                          >
                            <div className={`w-10 h-5 rounded-full relative transition-colors ${
                              preferences.analytics ? 'bg-blue-600' : 'bg-gray-300'
                            }`}>
                              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                                preferences.analytics ? 'translate-x-5' : 'translate-x-0.5'
                              }`} />
                            </div>
                          </button>
                        </div>
                      </div>

                      {/* Marketing Cookies */}
                      <div className="bg-white border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h3 className="text-sm font-medium text-gray-900">Marketing</h3>
                            <p className="text-xs text-gray-600">Targeted ads & campaigns</p>
                          </div>
                          <button
                            onClick={() => setPreferences(prev => ({ ...prev, marketing: !prev.marketing }))}
                            className="ml-3"
                          >
                            <div className={`w-10 h-5 rounded-full relative transition-colors ${
                              preferences.marketing ? 'bg-blue-600' : 'bg-gray-300'
                            }`}>
                              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                                preferences.marketing ? 'translate-x-5' : 'translate-x-0.5'
                              }`} />
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowSettings(false)}
                        className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveCustomPreferences}
                        className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-1"
                      >
                        <Check className="w-3.5 h-3.5" />
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;